import { DataManager } from '@syncfusion/ej2-data';
import { Component, ViewChild } from '@angular/core';
import { AdministrationItemType, DetailsInitInfo, ICachedItem, IItemData, ITreeNodeData, ItemEventInfo } from '../../data-structures';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { DragAndDropEventArgs, FieldsSettingsModel, ToolbarModule, TreeViewModule } from '@syncfusion/ej2-angular-navigations';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ClientService } from 'src/app/base/services/client/client.service';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ITreeViewComponent } from '../tree-view.interface';
import { TreeViewZoneBasedService } from './tree-view-zone-based.service';
import { TreeViewBaseComponent } from '../tree-view-base.component';
import { ParkingSubscriptionDTO, ParkingTariffDTO, ZoneDTO } from 'src/app/base/models/models.swagger.generated';
import { GenerateQrCodeDetailsComponent } from '../../administration-details/generate-qr-code-details/generate-qr-code-details.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass } from '@angular/common';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';

@Component({
    selector: 'app-tree-view-zone-based',
    templateUrl: './tree-view-zone-based.component.html',
    styleUrls: ['./tree-view-zone-based.component.scss'],
    standalone: true,
    imports: [TooltipModule, ToolbarModule, NgIf, TreeViewModule, NgClass, GenerateQrCodeDetailsComponent, TranslateModule]
})
export class TreeViewZoneBasedComponent extends TreeViewBaseComponent implements ITreeViewComponent {
  @ViewChild('generateQrCodeDialog') public generateQrCodeDialog: GenerateQrCodeDetailsComponent;

  public treeViewFieldSetting: FieldsSettingsModel;

  public canAddZone = false;
  public canAddDevice = false;
  public canAddTariff = false;
  public canRemoveTariff = false;
  public canAddSubscription = false;
  public canRemoveSubscription = false;
  public canGenerateQrCode = false;

  public tooltipAnimation = { open: { effect: 'FadeIn', delay: 500 }, close: { effect: 'None' } };

  private fieldNames: FieldsSettingsModel = {
    id: 'id',
    parentID: 'parentId',
  };

  private get itemCache() {
    return this.service.itemCache;
  }

  constructor(
    private service: TreeViewZoneBasedService,
    clientService: ClientService,
    private spinnerService: SpinnerService,
    modalService: ModalService,
    private toastService: ToastService
  ) {
    super(clientService, modalService);

    const dataSource = new DataManager({
      adaptor: new AvpCustomAdaptor(this.service, this.spinnerService)
    });
    this.treeViewFieldSetting = Object.assign({dataSource}, this.fieldNames);
    this.itemCache.resetCache();
  }

  public override reset() {
    this.itemCache.resetCache();
    super.reset();
  }

  // methods from ITreeViewComponent

  public addNode(eventInfo: ItemEventInfo): void {
    const parentId = this.parentInfo.item.id;
    const parentNodeId = this.parentInfo.item.treeNodeId;
    const createdNode = this.handleAddNodeEvent(eventInfo, this.service.getNextNodeId(eventInfo.id, eventInfo.type));
    if (createdNode) {
      // if node was not added to parent since it was not collapsed yet
      this.itemCache.addOrUpdateItem(createdNode.itemData);
    }
    if (eventInfo.type === AdministrationItemType.Subscription) {
      this.updateSubscriptionInfoOnTariffNodes(parentId, eventInfo.id, parentNodeId);
    }
  }

  public removeNode(eventInfo: ItemEventInfo): void {
    this.handleRemoveNodeEvent(eventInfo);
    if (eventInfo.type === AdministrationItemType.Subscription) {
      this.updateSubscriptionInfoOnTariffNodes(eventInfo.parentId, null);
    }
    this.itemCache.removeItem(eventInfo.id, eventInfo.type);
  }

  protected isItemInactive(eventInfo: ItemEventInfo): boolean {
    if (eventInfo.type === AdministrationItemType.Tariff || eventInfo.type === AdministrationItemType.Subscription) {
      return !(eventInfo.data as any).active;
    }
    if (eventInfo.type === AdministrationItemType.ZoneDevice) {
      return !(eventInfo.data as any).enabled;
    }
    return false;
  }

  public updateNode(eventInfo: ItemEventInfo): void {
    this.handleUpdateNodeEvent(eventInfo);

    if (eventInfo.type !== AdministrationItemType.Zone) {
      return;
    }

    if (eventInfo.type === AdministrationItemType.Zone) {
      const nodeData = this.tree.getTreeData(this.selectedItem.treeNodeId)?.at(0) as any as ITreeNodeData;
      if (!nodeData) {
        return;
      }

      const dto = eventInfo.data as ZoneDTO;
      if (dto.isGroupingElement) {
        // remove devices folder
        var childNodes = (nodeData as any).child as ITreeNodeData[];
        if (childNodes && childNodes.length > 0) {
          childNodes.forEach((child) => {
            if (child.itemData.type === AdministrationItemType.ZoneDeviceCollection) {
              this.tree.removeNodes([child.id]);
            }
          });
        }
      } else {
        // make sure device folder exists already or add folder if children are already loaded
        if (((nodeData as any)?.child as any[])?.length > 0) {
          let deviceNode = (nodeData as any).child?.find((child) => child.itemData.type === AdministrationItemType.ZoneDeviceCollection);
          if (!deviceNode) {
            // add device folder
            deviceNode = {
              id: this.service.getNextNodeId(dto.id, AdministrationItemType.ZoneDeviceCollection),
              hasChildren: dto.hasDevicesAssigned,
              itemData: {
                type: AdministrationItemType.ZoneDeviceCollection,
                name: 'i18n.domain.administration.overview.devices',
                inactive: false,
                dto: null
              }
            }
            this.tree.addNodes(deviceNode, nodeData.id as string, null, true);
          }
        } else {
          nodeData.hasChildren = dto.hasChildZones || dto.assignedTariffs?.length > 0 || !dto.isGroupingElement;
          this.tree.refreshNode(nodeData.id as string, nodeData as any);
        }
      }
    }

  }
  // end methods from ITreeViewComponent

  public onNodeDragStart(args: DragAndDropEventArgs) {
    const info = this.parseNodeId(args.draggedNodeData.id as string) as DetailsInitInfo;
    if (info.type === AdministrationItemType.ZoneCollection || info.type === AdministrationItemType.ZoneDeviceCollection) {
      args.cancel = true;
      return;
    }

    if (info.type === AdministrationItemType.Tariff) {
      const parentInfo = this.parseNodeId(args.draggedNodeData.parentID as string) as DetailsInitInfo;
      if (this.isCopyMode(args) || parentInfo.type === AdministrationItemType.TariffCollection) {
        // add tariffs to more zones is also allowed if tariff or its subscription is active
        return;
      }

      const nodeData = this.tree.getTreeData(args.draggedNodeData.id as string)?.at(0) as any as ITreeNodeData;
      let subscription: ICachedItem = null;
      if (nodeData?.itemData?.dto?.subscriptionId) {
        subscription = this.service.itemCache.getCachedItem(nodeData?.itemData?.dto?.subscriptionId, AdministrationItemType.Subscription);
      }

      const hasActiveSubscription = subscription != null ? !!subscription.itemData?.dto?.active : !!nodeData?.itemData?.dto?.hasActiveSubscription;
      if (hasActiveSubscription) {
        args.cancel = true;
        this.toastService.showWarning('i18n.domain.administration.overview.messages.active-item-cannot-be-moved-title', 'i18n.domain.administration.overview.messages.active-item-cannot-be-moved-message');
        return;
      }
    }

    if (info.type === AdministrationItemType.Subscription) {
      const nodeData = this.tree.getTreeData(args.draggedNodeData.id as string)?.at(0)?.itemData as IItemData;
      if (nodeData?.dto.active) {
        args.cancel = true;
        this.toastService.showWarning('i18n.domain.administration.overview.messages.active-item-cannot-be-moved-title', 'i18n.domain.administration.overview.messages.active-item-cannot-be-moved-message');
        return;
      }
    }
  }

  public override onNodeDragStop(args: DragAndDropEventArgs): void {
    super.onNodeDragStop(args);
    if (args.cancel) {
      return;
    }

    // all actions need to be handled within this method programmatically, because of proper positioning within parent node
    args.cancel = true;

    const isCopyMode = this.isCopyMode(args);
    const info = this.parseNodeId(args.draggedNodeData.id as string) as DetailsInitInfo;
    const targetInfo = this.parseNodeId(args.droppedNodeData.id as string) as DetailsInitInfo;
    if (isCopyMode) {
      switch (info.type) {
        case AdministrationItemType.Tariff:
          this.service.addTariffToZone(info.id, targetInfo.id).subscribe((treeNode) => {
            this.addNodeAtProperPositionByParentNodeId(treeNode, args.droppedNodeData.id as string);
          });
          return;
        case AdministrationItemType.Subscription:
          this.service.addSubscriptionToTariff(info.id, targetInfo.id).subscribe((treeNode) => {
            this.addNodeAtProperPositionByParentNodeId(treeNode, args.droppedNodeData.id as string);
            this.updateSubscriptionInfoOnTariffNodes(targetInfo.id, info.id, args.droppedNodeData.id as string);
          });
          return;
        default:
          return;
        }
    }

    let parentNodeId = null;
    let parentInfo: DetailsInitInfo = null;
    switch (info.type) {
      case AdministrationItemType.Zone:
        this.service.moveZone(info.id, targetInfo.id).subscribe(() => {
          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
        });
        return;
      case AdministrationItemType.ZoneDevice:
        this.service.moveZoneDevice(info.id, targetInfo.id).subscribe(() => {
          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
        });
        return;
      case AdministrationItemType.Tariff:
        parentNodeId = args.draggedNodeData.parentID;
        parentInfo = this.parseNodeId(parentNodeId as string) as DetailsInitInfo;
        this.service.moveTariff(info.id, parentInfo.id, targetInfo.id).subscribe(() => {
          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
        });
        return;
      case AdministrationItemType.Subscription:
        parentNodeId = args.draggedNodeData.parentID;
        parentInfo = this.parseNodeId(parentNodeId as string) as DetailsInitInfo;
        this.service.moveSubscription(info.id, parentInfo.id, targetInfo.id).subscribe(() => {
          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
          if (targetInfo.type === AdministrationItemType.Tariff) {
            this.updateSubscriptionInfoOnTariffNodes(targetInfo.id, info.id, args.draggedNodeData.id as string);
          }
          this.updateSubscriptionInfoOnTariffNodes(parentInfo.id, null);
        });
        return;
    }
  }

  public onRemoveTariff() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.administration.overview.messages.tariff-detach-title', 'i18n.domain.administration.overview.messages.tariff-detach-question');
    dialog.onOkSubject.subscribe(() => {
      this.service.moveTariff(this.selectedItem.id, this.selectedItem.parentId, null).subscribe(() => {
        this.tree.removeNodes([this.selectedItem.treeNodeId]);
        this.setSelectedItem(null);
        this.tree.selectedNodes = [];
      });
    });
  }

  public onRemoveSubscription() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.administration.overview.messages.subscription-detach-title', 'i18n.domain.administration.overview.messages.subscription-detach-question');
    dialog.onOkSubject.subscribe(() => {
      this.service.detachTariffFromSubscription(this.selectedItem.parentId, this.selectedItem.id).subscribe(() => {
        this.updateSubscriptionInfoOnTariffNodes(this.selectedItem.parentId, null);
        this.setSelectedItem(null);
        this.tree.selectedNodes = [];
      });
    });
  }

  public onGenerateQrCode() {
    const dto = this.selectedItem.itemData.dto as ZoneDTO;
    this.generateQrCodeDialog.openDialog(dto);
  }

  public onAddZone() {
    this.onAddNewItem(AdministrationItemType.Zone);
  }

  public onAddDevice() {
    this.onAddNewItem(AdministrationItemType.ZoneDevice);
  }

  public onAddTariff() {
    this.onAddNewItem(AdministrationItemType.Tariff);
  }

  public onAddSubscription() {
    this.onAddNewItem(AdministrationItemType.Subscription);
  }

  protected updateCanAddXyFlags() {
    // because of change after check issue, we need to use flags and cannot use getter methods
    const dto = this.selectedItem?.itemData?.dto;
    this.canAddZone = !!(this.selectedItem && (this.selectedItem.type === AdministrationItemType.ZoneCollection || (this.selectedItem?.id && this.selectedItem.type === AdministrationItemType.Zone)));
    this.canAddTariff = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Zone && dto && !dto.isGroupingElement);
    this.canRemoveTariff = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Tariff && dto && dto.assignedZones?.length > 1);
    this.canAddSubscription = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Tariff && dto && !dto.subscriptionId);
    this.canRemoveSubscription = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Subscription && dto?.tariffs?.length > 1);
    this.canGenerateQrCode = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Zone && dto && !dto.isGroupingElement)
    this.canAddDevice = this.selectedItem?.type === AdministrationItemType.ZoneDeviceCollection;
  }

  protected canDropNode(args: DragAndDropEventArgs): boolean {
    const draggedNode = args.draggedNodeData;
    const droppedNode = args.droppedNodeData;
    const isCopyMode = this.isCopyMode(args);
    if (draggedNode == null || droppedNode == null || draggedNode.parentID === droppedNode.id) {
      return false;
    }

    const info = this.parseNodeId(draggedNode.id as string) as DetailsInitInfo;
    const targetInfo = this.parseNodeId(droppedNode.id as string) as DetailsInitInfo;
    switch (info.type) {
      case AdministrationItemType.Zone:
        return (targetInfo.type === AdministrationItemType.ZoneCollection || targetInfo.type === AdministrationItemType.Zone) && !isCopyMode;
      case AdministrationItemType.ZoneDevice:
        return (targetInfo.type === AdministrationItemType.ZoneDeviceCollection) && !isCopyMode;
      case AdministrationItemType.Tariff: {
        if (targetInfo.type !== AdministrationItemType.TariffCollection && targetInfo.type !== AdministrationItemType.Zone) {
          return false;
        }
        const parentInfo = this.parseNodeId(draggedNode.parentID as string) as DetailsInitInfo;
        if (parentInfo.type === AdministrationItemType.TariffCollection && isCopyMode) {
          return false;
        }

        if (targetInfo.type === AdministrationItemType.TariffCollection) {
          // check if node can be added, i.e. no other node existing
          const nodeData = this.tree.getTreeData(draggedNode.id as string)?.at(0)?.itemData as IItemData;
          if (nodeData.dto.assignedZones && nodeData.dto.assignedZones.length > 1) {
            return false;
          }
        }

        const parentData = this.tree.getTreeData(droppedNode.id as string)?.at(0);
        if (!parentData) {
          return false;
        }
        const nodeData = this.tree.getTreeData(draggedNode.id as string)?.at(0)?.itemData as IItemData;
        const hasChildAlready = (parentData.child as any[])?.find((node) => node.itemData.type === nodeData.type && node.itemData.dto?.id === nodeData.dto?.id);
        if (hasChildAlready) {
          return false;
        }

        return (isCopyMode || !nodeData?.dto.hasActiveSubscription);
      }
      case AdministrationItemType.Subscription:
        if (targetInfo.type === AdministrationItemType.SubscriptionCollection && !isCopyMode) {
          const nodeData = this.tree.getTreeData(draggedNode.id as string)?.at(0)?.itemData as IItemData;
          return (nodeData?.dto as ParkingSubscriptionDTO)?.tariffs?.length === 1;
        }
        return targetInfo.type === AdministrationItemType.Tariff && !droppedNode.hasChildren;
      default:
        return false;
    }
  }

  protected parseNodeId(nodeId: string): DetailsInitInfo {
    return this.service.parseNodeId(nodeId);
  }

  private updateSubscriptionInfoOnTariffNodes(tariffId: number, subscriptionId: number, ignoreNodeId: string = null) {
    const affectedTariffs = this.findAllNodesOfTree(tariffId, AdministrationItemType.Tariff);
    affectedTariffs.forEach((tariff) => {
      if (tariff.id === ignoreNodeId) {
        return;
      }
      tariff.hasChildren = !!subscriptionId;
      if (!subscriptionId) {
        const child = (tariff as any).child?.at(0);
        if (child) {
          this.tree.removeNodes([child.id]);
        }
      }
      const dto = tariff.itemData?.dto as ParkingTariffDTO;
      if (dto) {
        dto.subscriptionId = subscriptionId;
      }
      this.tree.refreshNode(tariff.id, tariff as any);
    });
  }
}
